/*******************************************************************************
 * Copyright 2026 Collaterate. All rights reserved.
 *******************************************************************************/

// Product-discovery analytics bridge for the [[ product_recommendations ]],
// [[ semantic_search_results ]], [[ semantic_search_facets ]],
// [[ semantic_search_articles ]] and [[ popular_searches ]] theme components.
//
// Event catalog, payload shapes and the listener contract:
// docs/theme/product-discovery-theme-handoff.md, "The analytics listener contract".
//
// THREE SINKS: namespaced CustomEvents on `window`, the storefront's own
// `window.events` bus, and product-discovery's /events beacon. This file names no
// vendor, so a theme decides where the first two go.
//
// CONSENT gating works for those two -- a site simply does not subscribe. It does
// NOT work for the beacon: beacon() transmits from inside emit(), synchronously,
// before any listener runs, and there is no hook here by which a theme or a consent
// manager can suppress it. The only off switch is unsetting productDiscoveryGlobals
// .beaconUrl server-side, which is per environment rather than per shopper. It sends
// the shopper's raw search_term and the context token, which carries userId and
// segmentIds. Whether that is acceptable unconditionally is an open question; what is
// not in question is that gating a listener does not gate it.
//
// Every event is also QUEUED onto window.collaterateAnalytics.q, because these
// components are server-rendered and can emit before any listener exists. A late
// listener DRAINS that queue (ca.q.shift()) and then subscribes; one that reads
// without draining will double-count.
//
// Bundled into template-header.js by gulp-build-config.js, so it is present on
// every storefront page with no theme edit, and inert where there is no
// product-discovery markup.
//
// ES5 on purpose -- gulpfile.js lints at ecmaVersion 5 because UglifyJS 2.x
// cannot parse ES6+. No arrows, no const/let, no template literals.

(function (window, document) {
    'use strict';

    var QUEUE_GLOBAL = 'collaterateAnalytics';

    var EVENT_SEARCH_RESULTS_VIEWED = 'collaterate:search:results_viewed';
    var EVENT_RECS_IMPRESSION = 'collaterate:recs:impression';
    var EVENT_RECS_SELECT = 'collaterate:recs:select';
    var EVENT_FACET_SELECT = 'collaterate:search:facet_select';
    var EVENT_ARTICLES_VIEWED = 'collaterate:search:articles_viewed';
    var EVENT_ARTICLE_SELECT = 'collaterate:search:article_select';
    var EVENT_POPULAR_SELECT = 'collaterate:search:popular_select';
    // The applied-filters bar's three actions. Named collaterate:<value> (camelCase, no search: segment),
    // unlike the family above, which predates that convention and must not be renamed because themes
    // already listen on it.
    var EVENT_FILTER_REMOVED = 'collaterate:searchFilterRemoved';
    var EVENT_FILTERS_CLEARED = 'collaterate:searchFiltersCleared';
    var EVENT_SUGGESTION_ACCEPTED = 'collaterate:searchSuggestionAccepted';

    // The window.events constant each CustomEvent above is announced under. An explicit table, like
    // BEACON_TYPES: a rename on either side should be a visible edit here rather than a dead event.
    // EventVocabularyContractTest asserts each name is declared in templateHeader.js, because
    // events.trigger() is gated on isEventRegistered and an undeclared name is a silent no-op.
    var BUS_EVENTS = {
        'collaterate:search:results_viewed': 'SEARCH_RESULTS_VIEWED',
        'collaterate:recs:impression': 'RECOMMENDATIONS_VIEWED',
        'collaterate:recs:select': 'RECOMMENDATION_SELECTED',
        'collaterate:search:facet_select': 'SEARCH_FACET_SELECTED',
        'collaterate:search:articles_viewed': 'SEARCH_ARTICLES_VIEWED',
        'collaterate:search:article_select': 'SEARCH_ARTICLE_SELECTED',
        'collaterate:search:popular_select': 'POPULAR_SEARCH_SELECTED',
        'collaterate:searchFilterRemoved': 'SEARCH_FILTER_REMOVED',
        'collaterate:searchFiltersCleared': 'SEARCH_FILTERS_CLEARED',
        'collaterate:searchSuggestionAccepted': 'SEARCH_SUGGESTION_ACCEPTED'
    };

    var SELECTOR_RECS_WIDGET = '[data-collaterate-recs]';
    var SELECTOR_SEARCH_RESULTS = '[data-collaterate-search-results]';
    var SELECTOR_CARD = '[data-product-id]';
    var SELECTOR_SEARCH_FACETS = '[data-collaterate-search-facets]';
    var SELECTOR_SEARCH_APPLIED = '[data-collaterate-search-applied]';
    var SELECTOR_SEARCH_ARTICLES = '[data-collaterate-search-articles]';
    var SELECTOR_POPULAR_SEARCHES = '[data-collaterate-popular-searches]';
    var SELECTOR_FACET_LINK = '[data-facet-dimension]';
    var SELECTOR_ARTICLE_CARD = '[data-article-id]';
    var SELECTOR_POPULAR_LINK = '[data-popular-term]';

    // Half the widget visible is the threshold for "seen". Requiring the whole
    // widget would never fire for a widget taller than the viewport on a phone.
    var VISIBLE_RATIO = 0.5;

    // More than one threshold on purpose, and not a ladder of precision. intersectionRatio is
    // measured against the TARGET's own area, so a container taller than 2x the viewport can never
    // reach 0.5 of itself -- a 12-card grid in one column on a phone is already past that. The low
    // thresholds give a callback to re-evaluate at, where isSeen()'s screenful test can answer. With
    // a lone 0.5 threshold the browser delivers one entry and then nothing further.
    var SEEN_THRESHOLDS = [0, 0.05, 0.1, 0.25, VISIBLE_RATIO];

    // Expando marking a container whose impression already fired. An
    // IntersectionObserver re-fires every time an element scrolls back into
    // view, and one impression per scroll-past would inflate the count.
    var SEEN_FLAG = '__collaterateImpressionSent';

    /**
     * Pushes onto the global queue AND dispatches. See the contract at the top:
     * dispatch alone loses events emitted before a listener attached.
     */
    function emit(eventName, detail) {
        try {
            var bucket = window[QUEUE_GLOBAL] = window[QUEUE_GLOBAL] || {};
            bucket.q = bucket.q || [];
            bucket.q.push([eventName, detail]);
        } catch (queueError) {
            // Never let analytics break the page. A failure to record an event
            // is strictly less bad than a broken storefront.
            logSuppressed(queueError);
        }

        try {
            if (typeof window.CustomEvent === 'function') {
                window.dispatchEvent(new window.CustomEvent(eventName, { detail: detail }));
            }
        } catch (dispatchError) {
            logSuppressed(dispatchError);
        }

        try {
            announceOnBus(eventName);
        } catch (busError) {
            // A theme's own listener throwing must not cost the beacon its event, so this is its own
            // try rather than sharing one with the dispatch above.
            logSuppressed(busError);
        }

        try {
            beacon(eventName, detail);
        } catch (beaconError) {
            logSuppressed(beaconError);
        }
    }

    /**
     * Raises the matching event on window.events, the storefront's own bus.
     *
     * Guards on the CONSTANT and not merely on the bus: trigger() is gated on isEventRegistered,
     * which tests `this[eventName] != null`, so a name templateHeader.js never declared fires
     * nothing and reports nothing. A page whose theme never ran [[ template_header ]] has no bus at
     * all, which is the other half of the same guard.
     */
    function announceOnBus(eventName) {
        var bus = window.events;
        var constantName = BUS_EVENTS[eventName];
        if (!bus || !constantName || !bus[constantName]) {
            return;
        }
        bus.trigger(bus[constantName]);
    }

    // THE BEACON. navigator.sendBeacon rather than fetch, because a `select` fires as the shopper
    // navigates away, which cancels a fetch and loses exactly the event that matters. sendBeacon
    // cannot set headers, so the context token travels in the BODY -- which also avoids a preflight.
    //
    // An explicit name table rather than string munging, so a rename on either side is visible here.
    var BEACON_TYPES = {
        'collaterate:search:results_viewed': 'search_results_viewed',
        'collaterate:recs:impression': 'recs_impression',
        'collaterate:recs:select': 'recs_select',
        'collaterate:search:facet_select': 'facet_select',
        'collaterate:search:articles_viewed': 'articles_viewed',
        'collaterate:search:article_select': 'article_select',
        'collaterate:search:popular_select': 'popular_select'
    };

    // Buffered until the token arrives, then flushed. Bounded: if the token never
    // arrives, this must not grow with every page interaction.
    var MAX_BUFFERED = 20;
    var pending = [];
    var token = null;
    var tokenState = 'idle'; // idle | fetching | ready | failed
    var tokenMintedAt = 0;

    // The context token lives 900 seconds and the engine enforces `exp`. Refreshed at 14 minutes for
    // clock skew and beacons in flight, and refreshed PROACTIVELY because it cannot be refreshed
    // reactively: sendBeacon returns no response, so a rejection is invisible here. Without this a
    // page open past 15 minutes stops reporting, and the loss is biased -- the shopper who browses
    // for twenty minutes and then clicks is exactly the one dropped.
    var TOKEN_MAX_AGE_MS = 14 * 60 * 1000;

    function tokenIsStale() {
        return tokenState === 'ready' && (Date.now() - tokenMintedAt) >= TOKEN_MAX_AGE_MS;
    }

    // Published once per page by tag_includes/template_header.jsp, alongside searchGlobals. An
    // absent or empty value means "not configured" and disables the beacon rather than guessing a
    // URL, which is also what a header predating this global does -- the components keep rendering
    // and keep dispatching their CustomEvents regardless.
    function beaconUrl() {
        try {
            var globals = window.productDiscoveryGlobals;
            return (globals && globals.beaconUrl) || '';
        } catch (readError) {
            logSuppressed(readError);
            return '';
        }
    }

    function fetchToken() {
        if (tokenState !== 'idle') {
            return;
        }
        tokenState = 'fetching';
        try {
            var xhr = new window.XMLHttpRequest();
            // Same-origin: the monolith mints the token and derives the site from the
            // session, which is why the site is never sent from here -- that endpoint
            // rejects a caller-supplied siteId outright.
            //
            // Path is ApiUrls.REST_EXTERNAL + Realm.STOREFRONT + SEARCH_CONTEXT_TOKEN.
            xhr.open('GET', '/rest/storefront/search/contextToken', true);
            xhr.withCredentials = true;
            xhr.onreadystatechange = function () {
                if (xhr.readyState !== 4) {
                    return;
                }
                try {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        var parsed = JSON.parse(xhr.responseText);
                        // parsed.content: WrapperDTO serializes as {messages, errors, content}.
                        // RestEnvelopeContractTest derives the name from the DTO.
                        var value = parsed && parsed.content ? parsed.content.token : null;
                        if (value) {
                            token = value;
                            tokenMintedAt = Date.now();
                            tokenState = 'ready';
                            flush();
                            return;
                        }
                    }
                    // Terminal for this page view: retrying per event would turn one bad response
                    // into a request per interaction.
                    tokenState = 'failed';
                    pending.length = 0;
                } catch (parseError) {
                    tokenState = 'failed';
                    pending.length = 0;
                    logSuppressed(parseError);
                }
            };
            xhr.send();
        } catch (sendError) {
            tokenState = 'failed';
            pending.length = 0;
            logSuppressed(sendError);
        }
    }

    function post(events) {
        var url = beaconUrl();
        if (!url || !events.length) {
            return;
        }
        var payload = JSON.stringify({ token: token, events: events });
        // text/plain keeps this a CORS-simple request, so there is no preflight for
        // sendBeacon to fail on. The engine parses the body as JSON regardless of
        // the declared type.
        var blob = new window.Blob([payload], { type: 'text/plain;charset=UTF-8' });
        if (window.navigator && typeof window.navigator.sendBeacon === 'function') {
            window.navigator.sendBeacon(url, blob);
        }
        // No fetch fallback. On a browser without sendBeacon the events are dropped
        // rather than sent by a transport that a navigation would cancel -- partial
        // reporting that looks complete is worse than none.
    }

    function flush() {
        if (tokenState !== 'ready' || !pending.length) {
            return;
        }
        var batch = pending.splice(0, pending.length);
        post(batch);
    }

    function beacon(eventName, detail) {
        var type = BEACON_TYPES[eventName];
        if (!type || !beaconUrl()) {
            return;
        }
        // The engine takes the same field names the detail uses, so this is the detail plus a type
        // rather than a remapping. Every field a detail builder emits must be listed here, or the
        // engine silently never learns it. site_id is the one deliberate omission: the engine derives
        // the site from the token.
        var event = {
            type: type,
            search_term: detail.search_term,
            result_count: detail.result_count,
            recommendation_type: detail.recommendation_type,
            placement: detail.placement,
            items: detail.items,
            position: detail.position,
            facet_dimension: detail.facet_dimension,
            facet_value: detail.facet_value,
            facet_count: detail.facet_count,
            facet_selected: detail.facet_selected,
            article_id: detail.article_id
        };
        if (tokenState === 'failed') {
            return;
        }
        // Past its refresh age: drop back to idle so the fetch below mints a fresh one, and let this
        // event buffer behind it rather than posting with a token the engine will reject.
        if (tokenIsStale()) {
            token = null;
            tokenState = 'idle';
        }
        if (tokenState === 'ready') {
            post([event]);
            return;
        }
        if (pending.length < MAX_BUFFERED) {
            pending.push(event);
        }
        fetchToken();
    }

    function logSuppressed(error) {
        try {
            if (window.console && window.console.warn) {
                window.console.warn('product-discovery analytics suppressed an error', error);
            }
        } catch (ignored) {
            // Swallowing is the point; no-empty permits a comment-only block.
        }
    }

    function readAttribute(element, name) {
        var value = element.getAttribute(name);
        return value === null ? '' : value;
    }

    function readIntAttribute(element, name, fallback) {
        var parsed = parseInt(element.getAttribute(name), 10);
        return isNaN(parsed) ? fallback : parsed;
    }

    /**
     * Builds the `items` array from the cards inside a container, matched by the given selector and
     * keyed off the given id attribute. Reads position from the markup rather than from the loop
     * index so a paginated or reordered page reports absolute rank, which only the server knows.
     */
    function collectItems(container, selector, idAttribute) {
        var cards = container.querySelectorAll(selector);
        var items = [];
        var i;
        for (i = 0; i < cards.length; i++) {
            items.push({
                'id': readAttribute(cards[i], idAttribute),
                'position': readIntAttribute(cards[i], 'data-position', i)
            });
        }
        return items;
    }

    function recsDetail(widget) {
        return {
            'site_id': readIntAttribute(widget, 'data-site-id', 0),
            'recommendation_type': readAttribute(widget, 'data-recommendation-type'),
            'placement': readAttribute(widget, 'data-placement'),
            'items': collectItems(widget, SELECTOR_CARD, 'data-product-id')
        };
    }

    function searchDetail(container) {
        return {
            'site_id': readIntAttribute(container, 'data-site-id', 0),
            'search_term': readAttribute(container, 'data-search-term'),
            'result_count': readIntAttribute(container, 'data-result-count', 0),
            'items': collectItems(container, SELECTOR_CARD, 'data-product-id')
        };
    }

    function articlesDetail(container) {
        return {
            'items': collectItems(container, SELECTOR_ARTICLE_CARD, 'data-article-id')
        };
    }

    /**
     * Whether an entry represents the shopper actually having SEEN the widget.
     *
     * Not `isIntersecting` alone: the browser queues an entry whenever that flips, independently of
     * the threshold, and it is true when the target touches the viewport at all -- so the first pixel
     * of a widget at the bottom of the screen would count as an impression.
     *
     * Two ways to be seen, because one measure cannot cover both shapes. A widget shorter than the
     * viewport is judged by how much of ITSELF shows; one taller than 2x the viewport can never
     * satisfy that, so it is judged by whether it fills half the screen.
     */
    function isSeen(entry) {
        if (!entry.isIntersecting) {
            return false;
        }
        if (entry.intersectionRatio >= VISIBLE_RATIO) {
            return true;
        }
        var visibleHeight = entry.intersectionRect ? entry.intersectionRect.height : 0;
        // rootBounds is null in a cross-origin iframe, hence the fallback.
        var viewportHeight = (entry.rootBounds && entry.rootBounds.height) || window.innerHeight || 0;
        return viewportHeight > 0 && visibleHeight >= viewportHeight * VISIBLE_RATIO;
    }

    /**
     * Runs `callback` once the element counts as seen per isSeen(). Where IntersectionObserver is
     * unavailable, fires immediately: over-counting beats a silently missing event stream.
     */
    function whenSeen(element, callback) {
        if (typeof window.IntersectionObserver !== 'function') {
            callback();
            return;
        }
        var observer = new window.IntersectionObserver(function (entries) {
            var i;
            for (i = 0; i < entries.length; i++) {
                if (isSeen(entries[i])) {
                    observer.unobserve(entries[i].target);
                    callback();
                }
            }
        }, { threshold: SEEN_THRESHOLDS });
        observer.observe(element);
    }

    function fireOnce(container, eventName, detailBuilder) {
        if (container[SEEN_FLAG]) {
            return;
        }
        container[SEEN_FLAG] = true;
        emit(eventName, detailBuilder(container));
    }

    /**
     * Strips the brackets off an attribute selector like '[data-product-id]', so the SELECTOR_*
     * constants double as the single source of truth for both querySelectorAll and findCard.
     */
    function attributeOf(selector) {
        return selector.substring(1, selector.length - 1);
    }

    /**
     * Walks up from the click target to the ancestor (inclusive) carrying the given attribute.
     * Hand-rolled rather than Element.closest, for the older browsers the ES5 build targets.
     */
    function findCard(target, boundary, attribute) {
        var node = target;
        while (node && node !== boundary) {
            if (node.getAttribute && node.getAttribute(attribute) !== null) {
                return node;
            }
            node = node.parentNode;
        }
        return null;
    }

    /**
     * One delegated listener per widget rather than one per anchor: a card holds two links and a
     * theme may add more, so delegation keeps the count fixed and cannot miss one.
     */
    function bindRecsWidget(widget) {
        whenSeen(widget, function () {
            fireOnce(widget, EVENT_RECS_IMPRESSION, recsDetail);
        });

        widget.addEventListener('click', function (event) {
            try {
                var card = findCard(event.target, widget, attributeOf(SELECTOR_CARD));
                if (!card) {
                    return;
                }
                emit(EVENT_RECS_SELECT, {
                    'site_id': readIntAttribute(widget, 'data-site-id', 0),
                    'recommendation_type': readAttribute(widget, 'data-recommendation-type'),
                    'placement': readAttribute(widget, 'data-placement'),
                    'items': [{
                        'id': readAttribute(card, 'data-product-id'),
                        'position': readIntAttribute(card, 'data-position', 0)
                    }]
                });
            } catch (clickError) {
                // A click must navigate even if the analytics for it fails.
                logSuppressed(clickError);
            }
        });
    }

    function bindSearchResults(container) {
        whenSeen(container, function () {
            fireOnce(container, EVENT_SEARCH_RESULTS_VIEWED, searchDetail);
        });
    }

    /**
     * Delegated click listener on the facets sidebar. No impression: a facet list is not itself a
     * result set the shopper "sees" the way a card grid is, so only the click is reported.
     */
    function bindSearchFacets(container) {
        container.addEventListener('click', function (event) {
            try {
                var link = findCard(event.target, container, attributeOf(SELECTOR_FACET_LINK));
                if (!link) {
                    return;
                }
                emit(EVENT_FACET_SELECT, {
                    'facet_dimension': readAttribute(link, 'data-facet-dimension'),
                    // The RAW value, e.g. "250+" or a category id -- never the link's display label,
                    // which the engine's allowlist would silently reject.
                    'facet_value': readAttribute(link, 'data-facet-value'),
                    'facet_count': readIntAttribute(link, 'data-facet-count', 0),
                    // A boolean, not the attribute string: the JSP writes "true"/"false", and the
                    // truthy string 'false' would make every click read as selected.
                    'facet_selected': readAttribute(link, 'data-facet-selected') === 'true'
                });
            } catch (clickError) {
                // A click must navigate even if the analytics for it fails.
                logSuppressed(clickError);
            }
        });
    }

    /**
     * Delegated click listener on the applied-filters bar: removing a chip, Clear all, and accepting a
     * "Narrow to" suggestion. Each is raised BEFORE the browser follows the link, which is the moment
     * facet_select is raised, and carries ids and counts only: never a label, and never the query text.
     *
     * Bus and CustomEvent only. None of these is in BEACON_TYPES, so the engine's /events schema is
     * untouched. Nothing is raised for a stale-filter removal or a relaxed query, which are notices the
     * engine withheld the reason for.
     */
    function bindSearchApplied(container) {
        container.addEventListener('click', function (event) {
            try {
                var chip = findCard(event.target, container, 'data-chip-group');
                if (chip) {
                    emit(EVENT_FILTER_REMOVED, {
                        'groupId': readAttribute(chip, 'data-chip-group'),
                        'valueId': readAttribute(chip, 'data-chip-value')
                    });
                    return;
                }
                var clear = findCard(event.target, container, 'data-clear-all');
                if (clear) {
                    emit(EVENT_FILTERS_CLEARED, {
                        'removedCount': readIntAttribute(clear, 'data-removed-count', 0)
                    });
                    return;
                }
                var suggestion = findCard(event.target, container, 'data-suggestion-group');
                if (suggestion) {
                    emit(EVENT_SUGGESTION_ACCEPTED, {
                        'groupId': readAttribute(suggestion, 'data-suggestion-group'),
                        'valueId': readAttribute(suggestion, 'data-suggestion-value')
                    });
                }
            } catch (clickError) {
                // A click must navigate even if the analytics for it fails.
                logSuppressed(clickError);
            }
        });
    }

    function bindSearchArticles(container) {
        whenSeen(container, function () {
            fireOnce(container, EVENT_ARTICLES_VIEWED, articlesDetail);
        });

        container.addEventListener('click', function (event) {
            try {
                var card = findCard(event.target, container, attributeOf(SELECTOR_ARTICLE_CARD));
                if (!card) {
                    return;
                }
                var articleId = readAttribute(card, 'data-article-id');
                emit(EVENT_ARTICLE_SELECT, {
                    'article_id': articleId,
                    'items': [{
                        'id': articleId,
                        'position': readIntAttribute(card, 'data-position', 0)
                    }]
                });
            } catch (clickError) {
                // A click must navigate even if the analytics for it fails.
                logSuppressed(clickError);
            }
        });
    }

    function bindPopularSearches(container) {
        container.addEventListener('click', function (event) {
            try {
                var link = findCard(event.target, container, attributeOf(SELECTOR_POPULAR_LINK));
                if (!link) {
                    return;
                }
                emit(EVENT_POPULAR_SELECT, {
                    // search_term, not a field of its own: the engine's allowlist would drop it.
                    'search_term': readAttribute(link, 'data-popular-term'),
                    'position': readIntAttribute(link, 'data-position', 0)
                });
            } catch (clickError) {
                // A click must navigate even if the analytics for it fails.
                logSuppressed(clickError);
            }
        });
    }

    function init() {
        var widgets = document.querySelectorAll(SELECTOR_RECS_WIDGET);
        var results = document.querySelectorAll(SELECTOR_SEARCH_RESULTS);
        var facets = document.querySelectorAll(SELECTOR_SEARCH_FACETS);
        var applied = document.querySelectorAll(SELECTOR_SEARCH_APPLIED);
        var articles = document.querySelectorAll(SELECTOR_SEARCH_ARTICLES);
        var popular = document.querySelectorAll(SELECTOR_POPULAR_SEARCHES);
        var i;
        for (i = 0; i < widgets.length; i++) {
            bindRecsWidget(widgets[i]);
        }
        for (i = 0; i < results.length; i++) {
            bindSearchResults(results[i]);
        }
        for (i = 0; i < facets.length; i++) {
            bindSearchFacets(facets[i]);
        }
        for (i = 0; i < applied.length; i++) {
            bindSearchApplied(applied[i]);
        }
        for (i = 0; i < articles.length; i++) {
            bindSearchArticles(articles[i]);
        }
        for (i = 0; i < popular.length; i++) {
            bindPopularSearches(popular[i]);
        }

        // Mint the token now, not on the first event: a popular-searches header has no impression, so
        // its first event is a click, and minting lazily parks that behind a round trip the navigation
        // aborts. Only when there is something to report, so widget-free pages pay nothing.
        var hasMarkup = widgets.length || results.length || facets.length || articles.length || popular.length;
        if (hasMarkup && beaconUrl()) {
            fetchToken();
        }
    }

    function safeInit() {
        try {
            init();
        } catch (initError) {
            // The feature going quiet is acceptable; a throw out of a bundled header script would
            // break every later script in template-header.js on every storefront page.
            logSuppressed(initError);
        }
    }

    // Bundled into template-header.js, which runs in <head> before any widget markup exists. The
    // readyState check keeps this correct if the bundle is ever moved later in the page.
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', safeInit);
    } else {
        safeInit();
    }
}(window, document));
